<template>
  <div class="columns">
    <div class="column">
      <b-notification :closable="false">
        <b-field
          :label="$t('compAdminEntityEntityForm.label-1') + ' *'"
          :type="{ 'is-danger': errors.has('name') }"
          :message="errors.first('name')"
        >
          <b-input
            name="name"
            icon="id-badge"
            @input="$emit('input', buffer)"
            :placeholder="$t('compAdminEntityEntityForm.label-1') + '*'"
            v-model="buffer.name"
            v-validate="'required'"
            maxlength="50"
          />
        </b-field>
        <geonames-input
          :label="$t('compAdminEntityEntityForm.label-2')"
          :value="buffer.mainSite"
          :countryCode="buffer.country"
          @select="updateMarker"
          :allowedCountries="geonamesAllowedCountries"
        />
        <b-field
          :type="{ 'is-danger': errors.has('citySize') }"
          :message="errors.first('citySize')"
        >
          <template slot="label">
            {{ $t('compAdminEntityEntityForm.label-3') }} *
            <b-tooltip
              multilined
              :label="$t('compAdminEntityEntityForm.label-4')"
              type="is-black"
            >
              <b-icon icon="info-circle" />
            </b-tooltip>
          </template>
          <b-select
            name="citySize"
            v-model="buffer.citySize"
            v-validate="'required'"
            :placeholder="$t('compAdminEntityEntityForm.label-3') + '*'"
            @input="$emit('input', buffer)"
            expanded
          >
            <option value="0">
              {{ $t('compAdminEntityEntityForm.label-5') }}
            </option>
            <option value="1">
              {{ $t('compAdminEntityEntityForm.label-6') }}
            </option>
            <option value="2">
              {{ $t('compAdminEntityEntityForm.label-7') }}
            </option>
          </b-select>
        </b-field>
      </b-notification>
    </div>
    <div class="column">
      <l-map
        @click="changeMarker"
        ref="labLeaflet"
        :zoom="zoom"
        :center="center"
        style="z-index: 0"
        :style="[
          errorMarker
            ? { border: '1px solid #ff3880' }
            : { border: '1px solid #4280b0' }
        ]"
      >
        <l-tilelayer
          :url="jawgUrl"
          attribution="<a href='https://jawg.io?utm_medium=map&utm_source=attribution' title='Tiles Courtesy of Jawg Maps' target='_blank' class='jawg-attrib' >&copy; <b>Jawg</b>Maps</a> | <a href='https://www.openstreetmap.org/copyright' title='OpenStreetMap is open data licensed under ODbL' target='_blank' class='osm-attrib'>&copy; OSM contributors</a>"
        ></l-tilelayer>
        <l-marker
          :draggable="true"
          @drag="changeMarker"
          :lat-lng="currentMarker"
        >
        </l-marker>
      </l-map>
      <div
        v-if="errorAreaMarker"
        style="margin-top: 8px; color: #ff3860; font-size: 0.75rem"
      >
        {{ $t('compAdminEntityEntityForm.error-1') }}
      </div>
      <div
        v-else-if="errorMarker"
        style="margin-top: 8px; color: #ff3860; font-size: 0.75rem"
      >
        {{ $t('compAdminEntityEntityForm.error-2') }}
      </div>
    </div>
  </div>
</template>

<script>
import '@/plugins/leaflet.js'
import { useCoreStore } from '@/stores/core.js'
import { Entity } from '@/models/core'

const VALID_MARKER_AREA = 0.08

export default {
  name: 'EntityForm',
  inject: ['$validator'],
  components: {
    'l-map': () => import('vue2-leaflet').then(({ LMap }) => LMap),
    'l-tilelayer': () =>
      import('vue2-leaflet').then(({ LTileLayer }) => LTileLayer),
    'l-marker': () => import('vue2-leaflet').then(({ LMarker }) => LMarker),
    'geonames-input': () => import('@/components/GeonamesInput.vue')
  },
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      zoom: 5,
      errorAreaMarker: false,
      errorMarker: false,
      siteLatitude: Entity.DEFAULT_LATITUDE,
      siteLongitude: Entity.DEFAULT_LONGITUDE,
      buffer: Object.assign({}, this.value),
      jawgUrl: `https://tile.jawg.io/${process.env.VUE_APP_JAWG_MAP_STYLE}/{z}/{x}/{y}.png?access-token=${process.env.VUE_APP_JAWG_MAP_ACCESS_TOKEN}`
    }
  },
  setup () {
    const coreStore = useCoreStore()
    return { coreStore }
  },
  computed: {
    geonamesAllowedCountries: function () {
      return this.coreStore.setting('global', 'ALLOWED_COUNTRIES').value
    },
    currentMarker: function () {
      return [this.buffer.latitude, this.buffer.longitude]
    },
    center: function () {
      return [this.buffer.latitude, this.buffer.longitude]
    }
  },
  methods: {
    changeMarker (event) {
      if (
        this.siteLatitude !== Entity.DEFAULT_LATITUDE &&
        this.siteLongitude !== Entity.DEFAULT_LONGITUDE
      ) {
        if (
          event.latlng['lat'] <
            parseFloat(this.siteLatitude) + parseFloat(VALID_MARKER_AREA) &&
          event.latlng['lat'] >
            parseFloat(this.siteLatitude) - parseFloat(VALID_MARKER_AREA)
        ) {
          if (
            event.latlng['lng'] <
              parseFloat(this.siteLongitude) + parseFloat(VALID_MARKER_AREA) &&
            event.latlng['lng'] >
              parseFloat(this.siteLongitude) - parseFloat(VALID_MARKER_AREA)
          ) {
            this.buffer.latitude = event.latlng['lat']
            this.buffer.longitude = event.latlng['lng']
            this.$emit('input', this.buffer)
            this.errorMarker = false
            this.errorAreaMarker = false
          } else {
            this.errorMarker = true
            this.errorAreaMarker = true
          }
        } else {
          this.errorMarker = true
          this.errorAreaMarker = true
        }
      }
    },
    updateMarker (option) {
      if (option) {
        this.buffer.latitude = parseFloat(option.lat)
        this.buffer.longitude = parseFloat(option.lng)
        this.siteLatitude = parseFloat(option.lat)
        this.siteLongitude = parseFloat(option.lng)
        this.buffer.country = option.countryCode
        this.buffer.area = option.adminCode1
        this.buffer.mainSite = option.name
        this.$emit('input', this.buffer)
      }
    }
  },
  watch: {
    value: {
      handler () {
        this.buffer = Object.assign({}, this.value)
      },
      deep: true
    }
  },
  mounted () {
    this.siteLatitude = this.buffer.latitude + 0.000000001
    this.siteLongitude = this.buffer.longitude + 0.000000001
  }
}
</script>
